import React, { useEffect, useMemo, useState } from "react";
import { Grid, Typography } from "@mui/material";
import EntityHubLayout, { PAGE_SIZE } from "../../components/product-manager/EntityHubLayout";
import EntityAssetCard from "../../components/product-manager/EntityAssetCard";
import EditCategoryDialog from "../../components/product-manager/EditCategoryDialog";
import BulkEntityUploadDialog from "../../components/product-manager/BulkEntityUploadDialog";
import { CATEGORY_BULK_CONFIG } from "../../utils/bulkUpload/bulkEntityConfigs";
import {
  filterByHubState,
  useBookmarkableHubState,
} from "../../hooks/useBookmarkableHubState";
import { useCategories } from "../../hooks/useCategories";
import { apiService } from "../../api/services/apiService";
import type { Category } from "../../types";
import { EntityStatus } from "../../types";
import { pmHubAddPath } from "../../utils/pmHubRoutes";

const CategoryHubPage: React.FC = () => {
  const hubState = useBookmarkableHubState();
  const { categories: fetched, loading, error, refresh } = useCategories();
  const [categories, setCategories] = useState<Category[]>([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [toggleLoadingId, setToggleLoadingId] = useState<string | null>(null);

  useEffect(() => {
    setCategories(fetched);
  }, [fetched]);

  const filtered = useMemo(
    () => filterByHubState(categories, hubState),
    [categories, hubState],
  );

  const paginated = useMemo(() => {
    const start = (hubState.page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, hubState.page]);

  const handleToggle = async (category: Category) => {
    setToggleLoadingId(category.uuid);
    const newStatus =
      category.status === EntityStatus.ACTIVE
        ? EntityStatus.INACTIVE
        : EntityStatus.ACTIVE;
    try {
      const updated = await apiService.updateCategory(category.uuid, {
        name: category.name,
        status: newStatus,
      });
      setCategories((prev) =>
        prev.map((c) => (c.uuid === updated.uuid ? updated : c)),
      );
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Failed to update status");
    } finally {
      setToggleLoadingId(null);
    }
  };

  return (
    <>
      <EntityHubLayout
        hubTitle="Category Hub"
        hubDescription="Manage categories with individual and bulk create, update, and enable or disable."
        addPath={pmHubAddPath("category")}
        addLabel="Add Category"
        onBulkUpload={() => setBulkOpen(true)}
        loading={loading}
        error={error}
        itemCount={filtered.length}
        hubState={hubState}
      >
        <Grid container spacing={2}>
          {paginated.map((category) => (
            <Grid item xs={12} sm={6} md={4} key={category.uuid}>
              <EntityAssetCard
                title={category.name}
                status={category.status}
                enabled={category.status === EntityStatus.ACTIVE}
                toggleLoading={toggleLoadingId === category.uuid}
                onToggleEnabled={() => handleToggle(category)}
                onEdit={() => hubState.setEditUuid(category.uuid)}
                onDelete={async () => {
                  await apiService.deleteCategory(category.uuid);
                  setCategories((prev) =>
                    prev.filter((c) => c.uuid !== category.uuid),
                  );
                }}
              />
            </Grid>
          ))}
          {!loading && filtered.length === 0 && (
            <Grid item xs={12}>
              <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                No categories match your filters.
              </Typography>
            </Grid>
          )}
        </Grid>
      </EntityHubLayout>

      <BulkEntityUploadDialog
        open={bulkOpen}
        config={CATEGORY_BULK_CONFIG}
        onClose={() => setBulkOpen(false)}
        onComplete={() => refresh()}
      />

      <EditCategoryDialog
        categoryUuid={hubState.editUuid}
        open={Boolean(hubState.editUuid)}
        onClose={() => hubState.setEditUuid(null)}
        onSaved={(updated) =>
          setCategories((prev) =>
            prev.map((c) => (c.uuid === updated.uuid ? updated : c)),
          )
        }
      />
    </>
  );
};

export default CategoryHubPage;
